// CMS-Service für öffentliche (EE-kuratierte) Playlists — Admin-Endpoints /api/admin/playlists.
// Auth-Muster identisch zu audioService (eigene axios-Instanz + Bearer aus der Supabase-Session).
import axios, { AxiosInstance } from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getAccessToken } from '../lib/authToken';
import {
  Playlist,
  CreatePlaylistInput,
  UpdatePlaylistInput,
} from '../types/playlist';

class PlaylistService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: getApiBaseUrl(),
      timeout: 15000,
      headers: { 'Content-Type': 'application/json' },
    });
    this.api.interceptors.request.use(async (config) => {
      const token = await getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  }

  async getPlaylists(): Promise<Playlist[]> {
    const res = await this.api.get<{ data: Playlist[] }>('/api/admin/playlists');
    return res.data.data ?? [];
  }

  async getPlaylistById(id: string): Promise<Playlist> {
    const res = await this.api.get<{ data: Playlist }>(`/api/admin/playlists/${id}`);
    return res.data.data;
  }

  async createPlaylist(input: CreatePlaylistInput): Promise<Playlist> {
    const res = await this.api.post<{ data: Playlist }>('/api/admin/playlists', input);
    return res.data.data;
  }

  async updatePlaylist(id: string, input: UpdatePlaylistInput): Promise<Playlist> {
    const res = await this.api.patch<{ data: Playlist }>(`/api/admin/playlists/${id}`, input);
    return res.data.data;
  }

  async deletePlaylist(id: string): Promise<void> {
    await this.api.delete(`/api/admin/playlists/${id}`);
  }

  async addTrack(playlistId: string, trackId: string): Promise<Playlist> {
    const res = await this.api.post<{ data: Playlist }>(
      `/api/admin/playlists/${playlistId}/tracks`,
      { trackId }
    );
    return res.data.data;
  }

  async removeItem(playlistId: string, itemId: string): Promise<Playlist> {
    const res = await this.api.delete<{ data: Playlist }>(
      `/api/admin/playlists/${playlistId}/items/${itemId}`
    );
    return res.data.data;
  }

  async reorderItems(playlistId: string, itemIds: string[]): Promise<Playlist> {
    const res = await this.api.put<{ data: Playlist }>(
      `/api/admin/playlists/${playlistId}/items/reorder`,
      { itemIds }
    );
    return res.data.data;
  }

  // Spielbare Spuren (Tracks/Varianten) eines Produkts für den Track-Picker. WICHTIG: über
  // /api/content/:id (ContentService hängt tracks[] an) — /api/audio/:id (AudioController) tut das NICHT.
  async getContentTracks(
    contentId: string
  ): Promise<Array<{ id: string; kind: string; title?: string | null }>> {
    const res = await this.api.get(`/api/content/${contentId}`);
    const body = res.data as { data?: { tracks?: unknown[] }; tracks?: unknown[] };
    const tracks = (body?.data?.tracks ?? body?.tracks ?? []) as Array<{
      id: string;
      kind: string;
      title?: string | null;
      enabled?: boolean;
    }>;
    // NUR aktivierte Varianten: deaktivierte Track-Entitäten (enabled=false) sind Backfill-
    // Platzhalter — z.B. AUDIO/NATURE/FREQUENZ bei einem reinen Silent-Subliminal. Sie existieren
    // nicht als spielbares Produkt und dürfen nicht kuratierbar sein.
    return tracks.filter((t) => t.enabled !== false);
  }
}

export const playlistService = new PlaylistService();
